import React from "react";
import BaseEdge, { roundedOrthPath, roundTo } from "./BaseEdge";

/**
 * SideToSideEdge
 *
 * Behaves like mxEdgeStyle.SideToSide:
 * - Connects node side-to-side (left/right)
 * - Routes as: src -> (src.x, midY) -> (dst.x, midY) -> dst
 * - Supports edgeControls for cp1/cp2 (draggable)
 * - Full API parity with OrthogonalEdge (hover, drag, labels)
 */

function anchorSide(node, side /* "left"|"right" */) {
  const w = node.width;
  const h = node.height;
  if (side === "left") return { x: roundTo(node.x), y: roundTo(node.y + h / 2) };
  return { x: roundTo(node.x + w), y: roundTo(node.y + h / 2) };
}

export default function SideToSideEdge({
  edge,
  nodeById,
  edgeIndex,
  selectedIndex,
  onSelectEdge,
  onStartEdgeDrag,
  edgeControls,
  color,

  // hover props (EdgeRenderer provides)
  isHovered,
  onHoverEnter,
  onHoverLeave,
}) {
  const fromNode = nodeById[edge.from];
  const toNode = nodeById[edge.to];
  if (!fromNode || !toNode) return null;

  // Decide sides: prefer side-to-side
  const fromCenterX = fromNode.x + fromNode.width / 2;
  const toCenterX = toNode.x + toNode.width / 2;

  const fromSide = toCenterX >= fromCenterX ? "right" : "left";
  const toSide = fromSide === "right" ? "left" : "right";

  const src = anchorSide(fromNode, fromSide);
  const dst = anchorSide(toNode, toSide);

  // default mid Y between centers
  const defaultMidY = Math.round((src.y + dst.y) / 2);

  const ctrl = edgeControls?.[edgeIndex];

  // cp1 at (src.x, midY), cp2 at (dst.x, midY)
  const cp1 = ctrl?.cp1 ?? { x: src.x, y: defaultMidY };
  const cp2 = ctrl?.cp2 ?? { x: dst.x, y: defaultMidY };

  const pts = [src, cp1, cp2, dst];
  const d = roundedOrthPath(pts);

  const labelPos = {
    x: Math.round((cp1.x + cp2.x) / 2),
    y: Math.round((cp1.y + cp2.y) / 2) - 12,
  };

  return (
    <BaseEdge
      d={d}
      stroke={color}

      // hover
      isHovered={isHovered}
      onHoverEnter={onHoverEnter}
      onHoverLeave={onHoverLeave}

      onSelect={() => onSelectEdge(edgeIndex)}
      onStartDrag={(e) =>
        onStartEdgeDrag(edgeIndex, {
          clientX: e.clientX,
          clientY: e.clientY,
        })
      }
      label={edge.label}
      labelPos={labelPos}
    />
  );
}
